import Konva from 'konva';
import { jsPDF } from 'jspdf';
import type { CanvasShape, ExportFormat } from '../types';
import { pixelsToMetres, PIXELS_PER_METRE, DEFAULT_WALL_THICKNESS, dimensionStrings, roomTextLayout, sqmToSqft } from '../canvasConstants';
import { getSymbolPrimitives } from '../symbolGeometry';

/**
 * Floor plan export.
 *
 * Renders a clean, print-style version of the plan (white background, dark
 * lines) directly from the saved shapes — independent of the editor's current
 * zoom/pan or light/dark theme — and adds a professional title block with a
 * scale bar. PNG/PDF are rasterised from an offscreen Konva stage; SVG is true
 * vector. Both renderers consume the same geometry so output is identical.
 */

const PADDING = 80;
const TITLE_H = 72;
const MIN_W = 540;
const LINE = '#333333';
const SUBTLE = '#888888';
const ROOM_FILL = 'rgba(0,0,0,0.02)';
const INK = '#1a1a1a';
const SYMBOL_STROKE_WIDTH = 1.75;
const FONT = 'system-ui, -apple-system, sans-serif';

interface Bounds {
  minX: number;
  minY: number;
  drawW: number;
  drawH: number;
  width: number;
  height: number;
}

function rotate(px: number, py: number, deg: number): { x: number; y: number } {
  const r = (deg * Math.PI) / 180;
  return { x: px * Math.cos(r) - py * Math.sin(r), y: px * Math.sin(r) + py * Math.cos(r) };
}

function computeBounds(shapes: CanvasShape[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const extend = (x: number, y: number) => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  for (const s of shapes) {
    if (s.type === 'room') {
      const w = Math.max(0, s.width ?? 0);
      const h = Math.max(0, s.height ?? 0);
      extend(s.x, s.y);
      extend(s.x + w, s.y + h);
    } else if (s.type === 'symbol') {
      const w = Math.max(0, s.width ?? 0);
      const h = Math.max(0, s.height ?? 0);
      const rot = s.rotation ?? 0;
      for (const [lx, ly] of [
        [0, 0],
        [w, 0],
        [0, h],
        [w, h],
      ]) {
        const r = rotate(lx, ly, rot);
        extend(s.x + r.x, s.y + r.y);
      }
    } else if (s.points && s.points.length >= 2) {
      for (let i = 0; i + 1 < s.points.length; i += 2) extend(s.points[i], s.points[i + 1]);
    }
  }

  if (!Number.isFinite(minX)) {
    return { minX: 0, minY: 0, drawW: PADDING * 2, drawH: PADDING * 2, width: MIN_W, height: PADDING * 2 + TITLE_H };
  }

  const drawW = maxX - minX + PADDING * 2;
  const drawH = maxY - minY + PADDING * 2;
  return {
    minX,
    minY,
    drawW,
    drawH,
    width: Math.max(MIN_W, drawW),
    height: drawH + TITLE_H,
  };
}

function roomLabels(shape: CanvasShape): { label?: string; imperial?: string; metric?: string } {
  const w = Math.max(0, shape.width ?? 0);
  const h = Math.max(0, shape.height ?? 0);
  const { showDims } = roomTextLayout(w, h);
  const { imperial, metric } = dimensionStrings(w, h);
  return {
    label: shape.label ? shape.label.toUpperCase() : undefined,
    imperial: showDims ? imperial : undefined,
    metric: showDims ? metric : undefined,
  };
}

/** Sum of all room areas, in square metres. */
function grossInternalSqm(shapes: CanvasShape[]): number {
  let total = 0;
  for (const s of shapes) {
    if (s.type === 'room') total += pixelsToMetres(s.width ?? 0) * pixelsToMetres(s.height ?? 0);
  }
  return total;
}

/* ─── Konva (PNG/PDF) renderer ───────────────────────────── */

function renderStage(shapes: CanvasShape[], bounds: Bounds, name: string): Konva.Stage {
  const container = document.createElement('div');
  const stage = new Konva.Stage({ container, width: bounds.width, height: bounds.height });
  const layer = new Konva.Layer();
  layer.add(new Konva.Rect({ x: 0, y: 0, width: bounds.width, height: bounds.height, fill: '#ffffff' }));

  const ox = PADDING - bounds.minX + (bounds.width - bounds.drawW) / 2;
  const oy = PADDING - bounds.minY;

  // Walls first (under rooms), then rooms, symbols, dimensions.
  for (const s of shapes) {
    if (s.type === 'wall' && s.points && s.points.length >= 4) {
      const pts = s.points.map((p, i) => (i % 2 === 0 ? p + ox : p + oy));
      layer.add(new Konva.Line({ points: pts, stroke: LINE, strokeWidth: s.thickness ?? DEFAULT_WALL_THICKNESS, lineCap: 'butt', lineJoin: 'miter' }));
    }
  }
  for (const s of shapes) {
    if (s.type !== 'room') continue;
    const w = Math.max(0, s.width ?? 0);
    const h = Math.max(0, s.height ?? 0);
    layer.add(new Konva.Rect({ x: s.x + ox, y: s.y + oy, width: w, height: h, strokeEnabled: false, fill: ROOM_FILL }));
    const { label, imperial, metric } = roomLabels(s);
    const layout = roomTextLayout(w, h);
    const hasDims = !!(imperial && metric);
    if (label) {
      layer.add(new Konva.Text({ x: s.x + ox + 8, y: s.y + oy + layout.labelY, width: Math.max(20, w - 16), align: 'center', text: label, fontSize: layout.labelSize, fontStyle: 'bold', fontFamily: FONT, fill: INK }));
    }
    if (hasDims) {
      layer.add(new Konva.Text({ x: s.x + ox + 8, y: s.y + oy + layout.dimY, width: Math.max(20, w - 16), align: 'center', text: `${imperial}\n${metric}`, fontSize: layout.dimSize, lineHeight: 1.25, fontStyle: 'bold', fontFamily: FONT, fill: INK }));
    }
  }
  for (const s of shapes) {
    if (s.type !== 'symbol' || !s.symbolId) continue;
    const w = Math.max(0, s.width ?? 0);
    const h = Math.max(0, s.height ?? 0);
    const group = new Konva.Group({ x: s.x + ox, y: s.y + oy, rotation: s.rotation ?? 0 });
    const content = new Konva.Group({
      x: s.flipX ? w : 0,
      y: s.flipY ? h : 0,
      scaleX: s.flipX ? -1 : 1,
      scaleY: s.flipY ? -1 : 1,
    });
    for (const p of getSymbolPrimitives(s.symbolId, w, h, { stairDirection: s.stairDirection })) {
      if (p.k === 'hatch') content.add(new Konva.Rect({ x: p.x, y: p.y, width: p.w, height: p.h, fill: '#ffffff', listening: false }));
      else if (p.k === 'line') content.add(new Konva.Line({ points: p.pts, stroke: LINE, strokeWidth: SYMBOL_STROKE_WIDTH, lineCap: 'round', lineJoin: 'round' }));
      else if (p.k === 'rect') content.add(new Konva.Rect({ x: p.x, y: p.y, width: p.w, height: p.h, cornerRadius: p.r ?? 0, stroke: LINE, strokeWidth: SYMBOL_STROKE_WIDTH }));
      else if (p.k === 'circle') content.add(new Konva.Circle({ x: p.x, y: p.y, radius: p.r, stroke: LINE, strokeWidth: SYMBOL_STROKE_WIDTH }));
      else if (p.k === 'text') {
        const tw = p.text.length * p.size * 0.7 + 4;
        content.add(new Konva.Text({ x: p.x - tw / 2, y: p.y - p.size / 2, width: tw, align: 'center', text: p.text, fontSize: p.size, fontFamily: FONT, fill: LINE }));
      } else content.add(new Konva.Ellipse({ x: p.x, y: p.y, radiusX: p.rx, radiusY: p.ry, stroke: LINE, strokeWidth: SYMBOL_STROKE_WIDTH }));
    }
    group.add(content);
    layer.add(group);
  }
  for (const s of shapes) {
    if (s.type !== 'dimension' || !s.points || s.points.length < 4) continue;
    const [x1, y1, x2, y2] = s.points;
    const a1 = { x: x1 + ox, y: y1 + oy };
    const a2 = { x: x2 + ox, y: y2 + oy };
    const len = Math.hypot(a2.x - a1.x, a2.y - a1.y);
    if (len === 0) continue;
    const px = -(a2.y - a1.y) / len;
    const py = (a2.x - a1.x) / len;
    const t = 8;
    layer.add(new Konva.Line({ points: [a1.x, a1.y, a2.x, a2.y], stroke: INK, strokeWidth: 2 }));
    layer.add(new Konva.Line({ points: [a1.x + px * t, a1.y + py * t, a1.x - px * t, a1.y - py * t], stroke: INK, strokeWidth: 2 }));
    layer.add(new Konva.Line({ points: [a2.x + px * t, a2.y + py * t, a2.x - px * t, a2.y - py * t], stroke: INK, strokeWidth: 2 }));
    const label = `${pixelsToMetres(len).toFixed(2)} m`;
    const mx = (a1.x + a2.x) / 2;
    const my = (a1.y + a2.y) / 2;
    layer.add(new Konva.Rect({ x: mx - 36, y: my - 22, width: 72, height: 20, fill: '#ffffff' }));
    layer.add(new Konva.Text({ x: mx - 36, y: my - 20, width: 72, align: 'center', text: label, fontSize: 15, fontStyle: 'bold', fontFamily: FONT, fill: INK }));
  }

  // Title block.
  const tbY = bounds.drawH;
  const sqm = grossInternalSqm(shapes);
  const areaLine = sqm > 0 ? `Gross internal area ${Math.round(sqmToSqft(sqm))} sq ft (${sqm.toFixed(1)} m²)` : 'Floor plan';
  layer.add(new Konva.Line({ points: [PADDING / 2, tbY, bounds.width - PADDING / 2, tbY], stroke: LINE, strokeWidth: 1 }));
  layer.add(new Konva.Text({ x: PADDING / 2, y: tbY + 16, text: name || 'Floor plan', fontSize: 16, fontStyle: 'bold', fontFamily: FONT, fill: LINE }));
  layer.add(new Konva.Text({ x: PADDING / 2, y: tbY + 40, text: `${areaLine} • ${new Date().toLocaleDateString()}`, fontSize: 10, fontFamily: FONT, fill: SUBTLE }));
  addScaleBarKonva(layer, bounds.width - PADDING / 2 - PIXELS_PER_METRE, tbY + 44);

  stage.add(layer);
  return stage;
}

function addScaleBarKonva(layer: Konva.Layer, x: number, y: number) {
  const m = PIXELS_PER_METRE;
  layer.add(new Konva.Line({ points: [x, y, x + m, y], stroke: LINE, strokeWidth: 1.5 }));
  layer.add(new Konva.Line({ points: [x, y - 5, x, y + 5], stroke: LINE, strokeWidth: 1.5 }));
  layer.add(new Konva.Line({ points: [x + m / 2, y - 4, x + m / 2, y + 4], stroke: LINE, strokeWidth: 1 }));
  layer.add(new Konva.Line({ points: [x + m, y - 5, x + m, y + 5], stroke: LINE, strokeWidth: 1.5 }));
  layer.add(new Konva.Text({ x: x - 10, y: y - 20, text: '0', fontSize: 9, fontFamily: FONT, fill: SUBTLE }));
  layer.add(new Konva.Text({ x: x + m - 12, y: y - 20, text: '1 m', fontSize: 9, fontFamily: FONT, fill: SUBTLE }));
}

/* ─── SVG renderer ───────────────────────────────────────── */

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function svgPolyline(pts: number[]): string {
  const out: string[] = [];
  for (let i = 0; i + 1 < pts.length; i += 2) out.push(`${pts[i]},${pts[i + 1]}`);
  return out.join(' ');
}

function buildSvg(shapes: CanvasShape[], bounds: Bounds, name: string): string {
  const ox = PADDING - bounds.minX + (bounds.width - bounds.drawW) / 2;
  const oy = PADDING - bounds.minY;
  const parts: string[] = [`<rect width="${bounds.width}" height="${bounds.height}" fill="#ffffff"/>`];

  for (const s of shapes) {
    if (s.type === 'wall' && s.points && s.points.length >= 4) {
      const pts = s.points.map((p, i) => (i % 2 === 0 ? p + ox : p + oy));
      parts.push(`<polyline points="${svgPolyline(pts)}" fill="none" stroke="${LINE}" stroke-width="${s.thickness ?? DEFAULT_WALL_THICKNESS}" stroke-linejoin="miter"/>`);
    }
  }
  for (const s of shapes) {
    if (s.type !== 'room') continue;
    const w = Math.max(0, s.width ?? 0);
    const h = Math.max(0, s.height ?? 0);
    const x = s.x + ox;
    const y = s.y + oy;
    parts.push(`<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="rgba(0,0,0,0.02)" stroke="none"/>`);
    const { label, imperial, metric } = roomLabels(s);
    const layout = roomTextLayout(w, h);
    const hasDims = !!(imperial && metric);
    if (label) parts.push(`<text x="${x + w / 2}" y="${y + layout.labelY}" font-family="${FONT}" font-size="${layout.labelSize}" font-weight="bold" fill="${INK}" text-anchor="middle" dominant-baseline="hanging">${escapeXml(label)}</text>`);
    if (hasDims) {
      parts.push(`<text x="${x + w / 2}" y="${y + layout.dimY}" font-family="${FONT}" font-size="${layout.dimSize}" font-weight="bold" fill="${INK}" text-anchor="middle" dominant-baseline="hanging">${escapeXml(imperial!)}</text>`);
      parts.push(`<text x="${x + w / 2}" y="${y + layout.dimY + layout.dimSize + 4}" font-family="${FONT}" font-size="${layout.dimSize}" font-weight="bold" fill="${INK}" text-anchor="middle" dominant-baseline="hanging">${escapeXml(metric!)}</text>`);
    }
  }
  for (const s of shapes) {
    if (s.type !== 'symbol' || !s.symbolId) continue;
    const w = Math.max(0, s.width ?? 0);
    const h = Math.max(0, s.height ?? 0);
    const inner: string[] = [];
    for (const p of getSymbolPrimitives(s.symbolId, w, h, { stairDirection: s.stairDirection })) {
      if (p.k === 'hatch') inner.push(`<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="#ffffff" stroke="none"/>`);
      else if (p.k === 'line') inner.push(`<polyline points="${svgPolyline(p.pts)}" fill="none" stroke="${LINE}" stroke-width="${SYMBOL_STROKE_WIDTH}" stroke-linecap="round"/>`);
      else if (p.k === 'rect') inner.push(`<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" rx="${p.r ?? 0}" fill="none" stroke="${LINE}" stroke-width="${SYMBOL_STROKE_WIDTH}"/>`);
      else if (p.k === 'circle') inner.push(`<circle cx="${p.x}" cy="${p.y}" r="${p.r}" fill="none" stroke="${LINE}" stroke-width="${SYMBOL_STROKE_WIDTH}"/>`);
      else if (p.k === 'text') inner.push(`<text x="${p.x}" y="${p.y + p.size * 0.36}" font-family="${FONT}" font-size="${p.size}" fill="${LINE}" text-anchor="middle">${escapeXml(p.text)}</text>`);
      else inner.push(`<ellipse cx="${p.x}" cy="${p.y}" rx="${p.rx}" ry="${p.ry}" fill="none" stroke="${LINE}" stroke-width="${SYMBOL_STROKE_WIDTH}"/>`);
    }
    const fx = s.flipX ? w : 0;
    const fy = s.flipY ? h : 0;
    const sx = s.flipX ? -1 : 1;
    const sy = s.flipY ? -1 : 1;
    parts.push(`<g transform="translate(${s.x + ox} ${s.y + oy}) rotate(${s.rotation ?? 0}) translate(${fx} ${fy}) scale(${sx} ${sy})">${inner.join('')}</g>`);
  }
  for (const s of shapes) {
    if (s.type !== 'dimension' || !s.points || s.points.length < 4) continue;
    const [x1, y1, x2, y2] = s.points;
    const a1 = { x: x1 + ox, y: y1 + oy };
    const a2 = { x: x2 + ox, y: y2 + oy };
    const len = Math.hypot(a2.x - a1.x, a2.y - a1.y);
    if (len === 0) continue;
    const px = (-(a2.y - a1.y) / len) * 8;
    const py = ((a2.x - a1.x) / len) * 8;
    parts.push(`<line x1="${a1.x}" y1="${a1.y}" x2="${a2.x}" y2="${a2.y}" stroke="${INK}" stroke-width="2"/>`);
    parts.push(`<line x1="${a1.x + px}" y1="${a1.y + py}" x2="${a1.x - px}" y2="${a1.y - py}" stroke="${INK}" stroke-width="2"/>`);
    parts.push(`<line x1="${a2.x + px}" y1="${a2.y + py}" x2="${a2.x - px}" y2="${a2.y - py}" stroke="${INK}" stroke-width="2"/>`);
    const mx = (a1.x + a2.x) / 2;
    const my = (a1.y + a2.y) / 2;
    parts.push(`<rect x="${mx - 36}" y="${my - 22}" width="72" height="20" fill="#ffffff"/>`);
    parts.push(`<text x="${mx}" y="${my - 7}" font-family="${FONT}" font-size="15" font-weight="bold" fill="${INK}" text-anchor="middle">${pixelsToMetres(len).toFixed(2)} m</text>`);
  }

  // Title block.
  const tbY = bounds.drawH;
  const sqm = grossInternalSqm(shapes);
  const areaLine = sqm > 0 ? `Gross internal area ${Math.round(sqmToSqft(sqm))} sq ft (${sqm.toFixed(1)} m²)` : 'Floor plan';
  parts.push(`<line x1="${PADDING / 2}" y1="${tbY}" x2="${bounds.width - PADDING / 2}" y2="${tbY}" stroke="${LINE}" stroke-width="1"/>`);
  parts.push(`<text x="${PADDING / 2}" y="${tbY + 30}" font-family="${FONT}" font-size="16" font-weight="bold" fill="${LINE}">${escapeXml(name || 'Floor plan')}</text>`);
  parts.push(`<text x="${PADDING / 2}" y="${tbY + 49}" font-family="${FONT}" font-size="10" fill="${SUBTLE}">${escapeXml(areaLine)} • ${escapeXml(new Date().toLocaleDateString())}</text>`);
  const sx = bounds.width - PADDING / 2 - PIXELS_PER_METRE;
  const sy = tbY + 44;
  const m = PIXELS_PER_METRE;
  parts.push(`<line x1="${sx}" y1="${sy}" x2="${sx + m}" y2="${sy}" stroke="${LINE}" stroke-width="1.5"/>`);
  parts.push(`<line x1="${sx}" y1="${sy - 5}" x2="${sx}" y2="${sy + 5}" stroke="${LINE}" stroke-width="1.5"/>`);
  parts.push(`<line x1="${sx + m / 2}" y1="${sy - 4}" x2="${sx + m / 2}" y2="${sy + 4}" stroke="${LINE}" stroke-width="1"/>`);
  parts.push(`<line x1="${sx + m}" y1="${sy - 5}" x2="${sx + m}" y2="${sy + 5}" stroke="${LINE}" stroke-width="1.5"/>`);
  parts.push(`<text x="${sx}" y="${sy - 10}" font-family="${FONT}" font-size="9" fill="${SUBTLE}">0</text>`);
  parts.push(`<text x="${sx + m}" y="${sy - 10}" font-family="${FONT}" font-size="9" fill="${SUBTLE}" text-anchor="end">1 m</text>`);

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${bounds.width}" height="${bounds.height}" viewBox="0 0 ${bounds.width} ${bounds.height}">${parts.join('')}</svg>`;
}

/* ─── Download helpers ───────────────────────────────────── */

function sanitizeFilename(name: string): string {
  return (name || 'floor-plan').replace(/[^a-z0-9-_ ]/gi, '').trim().replace(/\s+/g, '-') || 'floor-plan';
}

function triggerDownload(href: string, filename: string) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function exportFloorPlan(shapes: CanvasShape[], format: ExportFormat, name: string): Promise<void> {
  const drawable = shapes.filter(
    (s) => s.type === 'room' || s.type === 'symbol' || (s.points != null && s.points.length >= 4)
  );
  if (drawable.length === 0) {
    throw new Error('Nothing to export — draw at least one room or wall first.');
  }

  const bounds = computeBounds(drawable);
  const base = sanitizeFilename(name);

  if (format === 'svg') {
    const svg = buildSvg(drawable, bounds, name);
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    triggerDownload(url, `${base}.svg`);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return;
  }

  const stage = renderStage(drawable, bounds, name);
  try {
    const dataUrl = stage.toDataURL({ pixelRatio: 2, mimeType: 'image/png' });
    if (format === 'png') {
      triggerDownload(dataUrl, `${base}.png`);
      return;
    }
    const landscape = bounds.width >= bounds.height;
    const pdf = new jsPDF({ orientation: landscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 12;
    const ratio = Math.min((pageW - margin * 2) / bounds.width, (pageH - margin * 2) / bounds.height);
    const imgW = bounds.width * ratio;
    const imgH = bounds.height * ratio;
    pdf.addImage(dataUrl, 'PNG', (pageW - imgW) / 2, (pageH - imgH) / 2, imgW, imgH);
    pdf.save(`${base}.pdf`);
  } finally {
    stage.destroy();
  }
}
